import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Card, Col, Popconfirm, Row, Table, TableColumnsType, TableProps, Typography } from "antd";
import axios from "axios";
import { message } from "components/Messages/Messages";
import MobileTable from "components/MobileTable/MobileTable";
import SearchBar from "components/SearchBar/SearchBar";
import useViewport from "components/UseViewport/UseViewport";
import { Event } from "interfaces/events";
import { useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";

const AdminEvents = () => {
  const { Title } = Typography;
  const history = useHistory();
  const { width } = useViewport();

  const [futureEvents, setFutureEvents] = useState<Event[]>([]);
  const [pastEvents, setPastEvents] = useState<Event[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [futurePagination, setFuturePagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [pastPagination, setPastPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchEvents = async (page = 1, pageSize = 10, search = "", ordering = "", timeFilter: "future" | "past") => {
    setLoading(true);
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
      time_filter: timeFilter,
    });

    if (search) {
      params.append("search", search);
    }

    if (ordering) {
      params.append("ordering", ordering);
    }

    try {
      const response = await axios.get<{ results: Event[]; count: number }>(
        `/events/admin-events/?${params.toString()}`
      );
      if (timeFilter === "future") {
        setFutureEvents(response.data.results);
        setFuturePagination(prev => ({
          ...prev,
          total: response.data.count,
        }));
      } else {
        setPastEvents(response.data.results);
        setPastPagination(prev => ({
          ...prev,
          total: response.data.count,
        }));
      }
    } catch (error) {
      message("error", "Kunne ikke hente arrangementer. Prøv igjen senere.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents(1, futurePagination.pageSize, "", "start", "future");
    fetchEvents(1, pastPagination.pageSize, "", "-start", "past");
  }, [futurePagination.pageSize, pastPagination.pageSize]);

  const handleSearch = (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
    fetchEvents(1, futurePagination.pageSize, newSearchTerm, "start", "future");
    fetchEvents(1, pastPagination.pageSize, newSearchTerm, "-start", "past");
  };

  const onRowClick = (eventID: number) => ({
    onClick: () => {
      history.push(`/admin/events/${eventID}`);
    },
    style: {
      cursor: "pointer",
    },
  });

  type OnChange = NonNullable<TableProps<Event>["onChange"]>;

  const handleFutureChange: OnChange = (tablePagination, filters, sorter) => {
    const ordering =
      sorter && !Array.isArray(sorter) && sorter.order
        ? `${sorter.order === "descend" ? "-" : ""}${sorter.field}`
        : "start";

    setFuturePagination(prev => ({
      ...prev,
      current: tablePagination.current || 1,
      pageSize: tablePagination.pageSize || 10,
    }));

    fetchEvents(tablePagination.current || 1, tablePagination.pageSize || 10, searchTerm, ordering, "future");
  };

  const handlePastChange: OnChange = (tablePagination, filters, sorter) => {
    const ordering =
      sorter && !Array.isArray(sorter) && sorter.order
        ? `${sorter.order === "descend" ? "-" : ""}${sorter.field}`
        : "-start";

    setPastPagination(prev => ({
      ...prev,
      current: tablePagination.current || 1,
      pageSize: tablePagination.pageSize || 10,
    }));

    fetchEvents(tablePagination.current || 1, tablePagination.pageSize || 10, searchTerm, ordering, "past");
  };

  const columns: TableColumnsType<Event> = [
    {
      title: "Visningsnavn",
      key: "name",
      dataIndex: "name",
      sorter: true,
      ellipsis: true,
    },
    {
      title: "Dato",
      dataIndex: "start",
      key: "start",
      sorter: true,
      render: (text, record) => {
        const date = new Date(record.start);
        return date.toLocaleDateString();
      },
      ellipsis: true,
      width: 200,
    },
    {
      key: "edit",
      render: (text, record) => {
        return (
          <Link to={`/admin/events/${record.id}/edit/`}>
            <EditOutlined style={{ fontSize: 20, cursor: "pointer" }} onClick={event => event.stopPropagation()} />
          </Link>
        );
      },
      width: 50,
    },
    {
      key: "x",
      render: (text, record) => {
        return (
          <Popconfirm
            key="delete-event"
            title={`Slett arrangementet "${record.name}"`}
            okText="Slett"
            placement="top"
            cancelText="Avbryt"
            style={{ backgroundColor: "var(--neutral-alt)" }}
            okType="danger"
            onConfirm={() => {
              axios
                .delete<Event>(`/events/admin-events/${record.id}/`)
                .then(() => {
                  message("success", "Arrangement slettet");
                  fetchEvents(futurePagination.current, futurePagination.pageSize, searchTerm, "start", "future");
                  fetchEvents(pastPagination.current, pastPagination.pageSize, searchTerm, "-start", "past");
                })
                .catch(() => message("error", "Kunne ikke slette arrangement. Prøv igjen senere."));
            }}
          >
            <DeleteOutlined
              style={{ color: "red", fontSize: 20, cursor: "pointer" }}
              onClick={event => event.stopPropagation()}
            />
          </Popconfirm>
        );
      },
      width: 50,
    },
  ];

  return (
    <>
      <Card>
        <Row justify="space-between" align="middle">
          <Title level={2} style={{ marginRight: "10px" }}>
            Arrangementer
          </Title>
          <Link to="/admin/events/new/">
            <Button type="primary" icon={<PlusOutlined />}>
              Opprett nytt arrangement
            </Button>
          </Link>
        </Row>
        <Row style={{ margin: "25px auto", width: "100%" }}>
          <SearchBar value={searchTerm} placeholder="Søk..." handleSearch={handleSearch} />
        </Row>
        <Row gutter={[16, 16]}>
          <Col lg={12} sm={24} style={{ width: "100%" }}>
            <Title level={4}>Fremtidige arrangementer</Title>
            {width > 768 ? (
              <Table
                columns={columns}
                dataSource={futureEvents}
                loading={loading}
                onRow={event => onRowClick(event.id)}
                onChange={handleFutureChange}
                pagination={futurePagination}
              />
            ) : (
              <MobileTable
                columns={[
                  {
                    title: "Visningsnavn",
                    dataIndex: "name",
                    key: "name",
                  },
                  {
                    title: "Dato",
                    dataIndex: "start",
                    key: "start",
                    render: value =>
                      value
                        ? new Date(value as string).toLocaleDateString("nb-NO", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "",
                  },
                  {
                    title: "Status",
                    dataIndex: "is_open",
                    key: "is_open",
                    render: value =>
                      value === 0 ? "Ikke åpnet" : value === 1 ? "Åpen" : value === 2 ? "Avmelding stengt" : "Stengt",
                  },
                  {
                    title: "Påmeldte",
                    dataIndex: "registrations",
                    key: "registrations",
                    render: (value, record) => `${record.registrations || 0}/${record.seats || 0}`,
                  },
                ]}
                data={futureEvents}
                loading={loading}
                pagination={{
                  ...futurePagination,
                  onChange: (page, pageSize) => {
                    setFuturePagination(prev => ({
                      ...prev,
                      current: page,
                      pageSize,
                    }));
                    fetchEvents(page, pageSize, searchTerm, "start", "future");
                  },
                }}
                onClick={id => history.push(`/admin/events/${id}/`)}
                emptyText="Ingen fremtidige arrangementer funnet"
              />
            )}
          </Col>
          <Col lg={12} sm={24} style={{ width: "100%" }}>
            <Title level={4}>Tidligere arrangementer</Title>
            {width > 768 ? (
              <Table
                columns={columns}
                dataSource={pastEvents}
                loading={loading}
                onRow={event => onRowClick(event.id)}
                onChange={handlePastChange}
                pagination={pastPagination}
              />
            ) : (
              <MobileTable
                columns={[
                  {
                    title: "Visningsnavn",
                    dataIndex: "name",
                    key: "name",
                  },
                  {
                    title: "Dato",
                    dataIndex: "start",
                    key: "start",
                    render: value =>
                      value
                        ? new Date(value as string).toLocaleDateString("nb-NO", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "",
                  },
                  {
                    title: "Status",
                    dataIndex: "is_open",
                    key: "is_open",
                    render: value =>
                      value === 0 ? "Ikke åpnet" : value === 1 ? "Åpen" : value === 2 ? "Avmelding stengt" : "Stengt",
                  },
                  {
                    title: "Påmeldte",
                    dataIndex: "registrations",
                    key: "registrations",
                    render: (value, record) => `${record.registrations || 0}/${record.seats || 0}`,
                  },
                ]}
                data={pastEvents}
                loading={loading}
                pagination={{
                  ...pastPagination,
                  onChange: (page, pageSize) => {
                    setPastPagination(prev => ({
                      ...prev,
                      current: page,
                      pageSize,
                    }));
                    fetchEvents(page, pageSize, searchTerm, "-start", "past");
                  },
                }}
                onClick={id => history.push(`/admin/events/${id}/`)}
                emptyText="Ingen tidligere arrangementer funnet"
              />
            )}
          </Col>
        </Row>
      </Card>
    </>
  );
};

export default AdminEvents;
